import React, { useState, useEffect, useCallback } from "react";
import { useIztro } from "iztro-hook";
import { AppContainer } from "./containers/AppContainer";
import { ResultContainer } from "./containers/ResultContainer";
import { AIChatProvider } from "./AIChat/AIChatContext";
import { useHistoryManager } from "./hooks/useHistoryManager";
import FormPanel from "./components/FormPanel";
import HistoryModal from "./components/HistoryModal";
import "./styles/index.css";
import "./styles/history-modal.css";
function App() {
    // 控制当前模式（排盘或合盘）
    const [chartMode, setChartMode] = useState("single");
    // 初始化表单状态
    const [formData, setFormData] = useState({
        name: "",
        year: "",
        month: "",
        day: "",
        birthTime: 0,
        birthdayType: "solar",
        gender: "male",
    });
    // 初始化合盘表单状态
    const [synastryData, setSynastryData] = useState({
        person1: {
            name: "",
            year: "",
            month: "",
            day: "",
            birthTime: 0,
            birthdayType: "solar",
            gender: "male",
        },
        person2: {
            name: "",
            year: "",
            month: "",
            day: "",
            birthTime: 0,
            birthdayType: "solar",
            gender: "female",
        },
    });
    // 控制星盘显示的状态
    const [showAstrolabe, setShowAstrolabe] = useState(false);
    // 控制输入面板显示的状态
    const [showInputPanel, setShowInputPanel] = useState(true);
    // 使用历史管理Hook
    const { history, showHistoryModal, setShowHistoryModal, searchTerm, setSearchTerm, showPasswordInput, setShowPasswordInput, password, setPassword, isCloudStorageEnabled, isLoading, editingHistoryId, setEditingHistoryId, handlePasswordSubmit, handleSaveHistory, handleDeleteHistory, handleClearHistory, } = useHistoryManager();
    // 获取星盘数据
    const { astrolabe } = useIztro({
        birthday: `${formData.year}-${String(formData.month).padStart(2, '0')}-${String(formData.day).padStart(2, '0')}`,
        birthTime: formData.birthTime,
        gender: formData.gender,
        birthdayType: formData.birthdayType,
    });
    // 获取第一个人星盘数据
    const { astrolabe: astrolabe1 } = useIztro({
        birthday: `${synastryData.person1.year}-${String(synastryData.person1.month).padStart(2, '0')}-${String(synastryData.person1.day).padStart(2, '0')}`,
        birthTime: synastryData.person1.birthTime,
        gender: synastryData.person1.gender,
        birthdayType: synastryData.person1.birthdayType,
    });
    // 获取第二个人星盘数据
    const { astrolabe: astrolabe2 } = useIztro({
        birthday: `${synastryData.person2.year}-${String(synastryData.person2.month).padStart(2, '0')}-${String(synastryData.person2.day).padStart(2, '0')}`,
        birthTime: synastryData.person2.birthTime,
        gender: synastryData.person2.gender,
        birthdayType: synastryData.person2.birthdayType,
    });
    // 从URL参数中读取数据并设置表单状态
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        // 从URL参数中读取各个字段
        const name = urlParams.get('n') || "";
        const year = urlParams.get('y') || "";
        const month = urlParams.get('m') || "";
        const day = urlParams.get('d') || "";
        const birthTime = urlParams.get('t') ? parseInt(urlParams.get('t') || "1") : 0;
        const birthdayType = urlParams.get('type') || "solar";
        const gender = urlParams.get('g') || "male";
        // 检查是否有完整的日期参数（年、月、日都是必需的）
        const hasCompleteDateParams = !!(urlParams.get('y') && urlParams.get('m') && urlParams.get('d'));
        setFormData({
            name,
            year,
            month,
            day,
            birthTime,
            birthdayType,
            gender,
        });
        // 只有当日期参数完整时才显示星盘，否则显示输入页面
        if (hasCompleteDateParams) {
            setShowAstrolabe(true);
            setShowInputPanel(false);
        }
        else {
            setShowAstrolabe(false);
            setShowInputPanel(true);
        }
    }, []);
    // 动态更新主题颜色以适应暗色模式
    useEffect(() => {
        const updateThemeColor = (isDarkMode) => {
            const themeColor = isDarkMode ? "#1B1B1B" : "#ffffff";
            let metaThemeColor = document.querySelector("meta[name=theme-color]");
            if (metaThemeColor) {
                metaThemeColor.setAttribute("content", themeColor);
            }
            else {
                metaThemeColor = document.createElement("meta");
                metaThemeColor.setAttribute("name", "theme-color");
                metaThemeColor.setAttribute("content", themeColor);
                document.getElementsByTagName("head")[0].appendChild(metaThemeColor);
            }
        };
        const darkModeMediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        // 初始化主题颜色
        updateThemeColor(darkModeMediaQuery.matches);
        // 监听主题变化
        const darkModeListener = (e) => {
            updateThemeColor(e.matches);
        };
        darkModeMediaQuery.addEventListener("change", darkModeListener);
        // 清理监听器
        return () => {
            darkModeMediaQuery.removeEventListener("change", darkModeListener);
        };
    }, []);
    // 将表单数据编码到URL参数中
    const updateUrlParams = useCallback((data) => {
        try {
            // 构建查询参数
            const params = new URLSearchParams();
            // 添加所有参数
            if (data.name)
                params.set('n', data.name);
            params.set('y', data.year);
            params.set('m', data.month);
            params.set('d', data.day);
            params.set('t', data.birthTime.toString());
            params.set('type', data.birthdayType);
            params.set('g', data.gender);
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            window.history.replaceState({}, '', newUrl);
        }
        catch (error) {
            console.error("编码URL参数失败:", error);
        }
    }, []);
    // 处理历史记录加载
    const handleLoadHistory = useCallback((item) => {
        if (item.type === 'single') {
            setFormData({
                name: item.name,
                year: item.year,
                month: item.month,
                day: item.day,
                birthTime: item.birthTime,
                birthdayType: item.birthdayType,
                gender: item.gender,
            });
            setChartMode('single');
            updateUrlParams({
                name: item.name,
                year: item.year,
                month: item.month,
                day: item.day,
                birthTime: item.birthTime,
                birthdayType: item.birthdayType,
                gender: item.gender,
            });
            setShowAstrolabe(true);
            setShowInputPanel(false);
        }
        else {
            setSynastryData({
                person1: {
                    name: item.name,
                    year: item.year,
                    month: item.month,
                    day: item.day,
                    birthTime: item.birthTime,
                    birthdayType: item.birthdayType,
                    gender: item.gender,
                },
                person2: item.person2 || {
                    name: "",
                    year: "",
                    month: "",
                    day: "",
                    birthTime: 0,
                    birthdayType: "solar",
                    gender: "female",
                }
            });
            setChartMode('synastry');
            setShowAstrolabe(true);
            setShowInputPanel(false);
        }
    }, [updateUrlParams]);
    // 处理历史记录编辑
    const handleEditHistory = useCallback((item) => {
        if (item.type === 'single') {
            setFormData({
                name: item.name,
                year: item.year,
                month: item.month,
                day: item.day,
                birthTime: item.birthTime,
                birthdayType: item.birthdayType,
                gender: item.gender,
            });
            setChartMode('single');
        }
        else {
            setSynastryData({
                person1: {
                    name: item.name,
                    year: item.year,
                    month: item.month,
                    day: item.day,
                    birthTime: item.birthTime,
                    birthdayType: item.birthdayType,
                    gender: item.gender,
                },
                person2: item.person2 || {
                    name: "",
                    year: "",
                    month: "",
                    day: "",
                    birthTime: 0,
                    birthdayType: "solar",
                    gender: "female",
                }
            });
            setChartMode('synastry');
        }
    }, []);
    // 处理显示输入面板
    const handleShowInputPanel = useCallback(() => {
        setShowInputPanel(true);
        setShowAstrolabe(false);
        window.history.replaceState({}, '', window.location.pathname);
        setEditingHistoryId(null);
    }, []);
    return (React.createElement(AppContainer, null,
        React.createElement(AIChatProvider, null,
            React.createElement("div", { className: "content" },
                showInputPanel && (React.createElement(FormPanel, { chartMode: chartMode, setChartMode: setChartMode, formData: formData, synastryData: synastryData, onFormChange: setFormData, onSynastryChange: (person, data) => setSynastryData({
                        ...synastryData,
                        [person]: data
                    }), onRadioChange: (name, value) => setFormData({
                        ...formData,
                        [name]: value
                    }), onSynastryRadioChange: (person, name, value) => setSynastryData({
                        ...synastryData,
                        [person]: {
                            ...synastryData[person],
                            [name]: value
                        }
                    }), onShowHistory: () => setShowHistoryModal(true), onSubmit: (e) => e.preventDefault(), onSynastrySubmit: (e) => e.preventDefault(), onSaveHistory: handleSaveHistory, updateUrlParams: updateUrlParams, setShowAstrolabe: setShowAstrolabe, setShowInputPanel: setShowInputPanel })),
                React.createElement(HistoryModal, { history: history, showHistoryModal: showHistoryModal, setShowHistoryModal: setShowHistoryModal, searchTerm: searchTerm, setSearchTerm: setSearchTerm, showPasswordInput: showPasswordInput, setShowPasswordInput: setShowPasswordInput, password: password, setPassword: setPassword, isCloudStorageEnabled: isCloudStorageEnabled, isLoading: isLoading, editingHistoryId: editingHistoryId, setEditingHistoryId: setEditingHistoryId, handlePasswordSubmit: handlePasswordSubmit, handleDeleteHistory: handleDeleteHistory, handleClearHistory: handleClearHistory, onLoadHistory: handleLoadHistory, onEditHistory: handleEditHistory, chartMode: chartMode }),
                showAstrolabe && (React.createElement(ResultContainer, { chartMode: chartMode, formData: formData, synastryData: synastryData, astrolabe: astrolabe, astrolabe1: astrolabe1, astrolabe2: astrolabe2, onShowInputPanel: handleShowInputPanel }))))));
}
export default React.memo(App);
